import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";

const Prev = ({ data, id }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB in bytes
  const ALLOWED_TYPES = ["image/jpeg", "image/jpg"];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: "numeric", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const options = { hour: "numeric", minute: "numeric", hour12: true };
    return date.toLocaleTimeString("en-US", options);
  };

  const uploadImageMutation = useMutation({
    mutationFn: async (file) => {
      const token = localStorage.getItem("refID");
      const formData = new FormData();
      formData.append("path", file);

      const response = await axios.post(
        `https://platinumfuse.v2napi.com/api/events/${id}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      // console.log("Image uploaded successfully:", data);
      setIsUploading(false);
      setErrorMessage("");
    },
    onError: (error) => {
      // console.error("Error uploading image:", error);
      setIsUploading(false);
      setErrorMessage(
        error?.response?.data?.message || "Error uploading image"
      );
    },
  });

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        message: "Only JPG/JPEG images are allowed.",
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        message: "Image size must be less than 3MB.",
      };
    }

    return { valid: true };
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setErrorMessage("");
    let validFiles = [];
    let hasInvalidFiles = false;

    // Validate each file
    for (let i = 0; i < files.length; i++) {
      const validation = validateFile(files[i]);
      if (validation.valid) {
        validFiles.push(files[i]);
      } else {
        hasInvalidFiles = true;
        setErrorMessage(validation.message);
        break;
      }
    }

    // Only proceed if all files are valid
    if (validFiles.length > 0 && !hasInvalidFiles) {
      setIsUploading(true);
      // Upload each valid file
      for (let i = 0; i < validFiles.length; i++) {
        uploadImageMutation.mutate(validFiles[i]);
      }
    }

    // Reset the input field to allow uploading the same file again if needed
    e.target.value = "";
  };

  return (
    <div className="w-full px-4 sm:px-10 pt-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
        {/* Left Column: Date, Time, Title, and Venue */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
            <div className="flex flex-col space-y-2">
              <label className="text-white">Date</label>
              <p className="text-gray-500 py-3 px-2 font-bold">
                {formatDate(data?.starts_at)}
              </p>
            </div>
            <div className="flex flex-col space-y-2">
              <label className="text-white">Time</label>
              <p className="text-gray-500 py-3 px-2 font-bold">
                {formatTime(data?.starts_at)}
              </p>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-white">Title</label>
            <p className="text-gray-500 py-3 px-2 font-bold">{data?.name}</p>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-white">Venue</label>
            <p className="text-gray-500 py-3 px-2 font-bold max-w-md">
              {data?.venue}
            </p>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-white">Description</label>
            <p className="text-gray-500 py-3 px-2 font-bold">
              {data?.description}
            </p>
          </div>
        </div>

        {/* Image Upload Area */}
        <div className="flex flex-col space-y-4">
          <label className="text-white font-medium">Event Images</label>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-500 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer relative">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              accept="image/jpeg,image/jpg"
            />

            <div className="flex flex-col items-center text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-gray-400 mt-2">
                Click or drag images to upload
              </p>
              <p className="text-gray-500 text-sm mt-1">
                JPG/JPEG only, max 3MB
              </p>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-900 bg-opacity-30 rounded-lg p-2 text-center">
              <p className="text-red-400 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Upload Status */}
          {isUploading && (
            <div className="bg-gray-700 rounded-lg p-2 text-center">
              <p className="text-amber-500">Uploading...</p>
            </div>
          )}

          {/* Display Images from API */}
          {data?.images && data.images.length > 0 && (
            <div className="space-y-2">
              <p className="text-white">Event Images ({data.images.length})</p>
              <div className="grid grid-cols-2 gap-2">
                {data.images.map((image, index) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.url}
                      alt={`Event ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                      <span className="text-white text-xs">
                        Image {index + 1}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Prev;
